<template>
    <div class="banner-management">
      <div class="page-header">
        <div>
          <h2 class="page-title">Banner管理</h2>
          <p class="page-desc">管理首页轮播图，支持跳转商品、活动或外链</p>
        </div>
        <el-button type="primary" size="large" @click="openAddDialog">
          <el-icon><Plus /></el-icon>
          新增Banner
        </el-button>
      </div>
  
      <!-- 统计卡片 -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-value">{{ stats.total || 0 }}</div>
          <div class="stat-label">总数</div>
        </div>
        <div class="stat-card success">
          <div class="stat-value">{{ stats.active || 0 }}</div>
          <div class="stat-label">已启用</div>
        </div>
      </div>
  
      <!-- 搜索筛选栏 -->
      <div class="filter-bar">
        <el-input 
          v-model="searchKey" 
          placeholder="搜索标题"
          prefix-icon="Search"
          clearable
          style="width: 260px"
          @keyup.enter="fetchData"
        />
        <el-select v-model="positionFilter" placeholder="全部位置" clearable style="width: 120px" @change="fetchData">
          <el-option label="首页" value="home" />
          <el-option label="活动页" value="activity" />
        </el-select>
        <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px" @change="fetchData">
          <el-option label="启用" :value="1" />
          <el-option label="禁用" :value="0" />
        </el-select>
        <el-button type="primary" @click="fetchData">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </div>
  
      <!-- Banner列表 -->
      <el-table :data="bannerList" border v-loading="loading" style="width: 100%">
        <el-table-column label="图片" width="120" align="center">
          <template #default="{ row }">
            <el-image :src="row.image_url" fit="cover" style="width: 80px; height: 60px; border-radius: 6px;" />
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="150" show-overflow-tooltip />
        <el-table-column label="链接类型" width="100" align="center">
          <template #default="{ row }">
            <el-tag size="small">{{ getLinkTypeText(row.link_type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="link_value" label="链接值" min-width="200" show-overflow-tooltip />
        <el-table-column label="位置" width="100" align="center">
          <template #default="{ row }">
            {{ row.position === 'home' ? '首页' : '活动页' }}
          </template>
        </el-table-column>
        <el-table-column label="排序" width="80" align="center">
          <template #default="{ row }">
            {{ row.sort_order }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right" align="center">
          <template #default="{ row }">
            <el-button type="primary" link size="small" @click="openEditDialog(row)">
              <el-icon><Edit /></el-icon> 编辑
            </el-button>
            <el-popconfirm title="确定删除该Banner吗？" @confirm="handleDelete(row.id)">
              <template #reference>
                <el-button type="danger" link size="small">
                  <el-icon><Delete /></el-icon> 删除
                </el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
  
      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="pagination.total > pagination.limit">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.limit"
          :total="pagination.total"
          :page-sizes="[10, 20, 50]"
          layout="total, sizes, prev, pager, next"
          @size-change="fetchData"
          @current-change="fetchData"
        />
      </div>
  
      <!-- 新增/编辑弹窗 -->
      <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑Banner' : '新增Banner'" width="600px" destroy-on-close>
        <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
          <el-form-item label="标题" prop="title">
            <el-input v-model="formData.title" placeholder="请输入标题（可选）" />
          </el-form-item>
  
          <el-form-item label="Banner图片" prop="image_url" required>
            <el-upload
              :file-list="imageFiles"
              action="#"
              :limit="1"
              accept="image/*"
              :auto-upload="false"
              :on-change="handleImageChange"
              :on-remove="() => formData.image_url = ''"
              list-type="picture-card"
            >
              <el-icon><Plus /></el-icon>
              <template #tip>
                <div class="upload-tip">建议尺寸 750x350px，支持JPG/PNG</div>
              </template>
            </el-upload>
          </el-form-item>
  
          <el-form-item label="链接类型" prop="link_type" required>
            <el-select v-model="formData.link_type" placeholder="请选择链接类型" style="width: 100%" @change="onLinkTypeChange">
              <el-option label="商品详情" value="product" />
              <el-option label="活动详情" value="activity" />
              <el-option label="外部链接" value="url" />
            </el-select>
          </el-form-item>
  
          <el-form-item label="链接值" prop="link_value" required>
            <el-select 
              v-if="formData.link_type === 'product'"
              v-model="formData.link_value" 
              placeholder="请选择商品" 
              filterable
              style="width: 100%"
            >
              <el-option v-for="product in productOptions" :key="product.id" :label="product.product_name" :value="`product_${product.id}`" />
            </el-select>
            <el-select 
              v-else-if="formData.link_type === 'activity'"
              v-model="formData.link_value" 
              placeholder="请选择活动" 
              filterable
              style="width: 100%"
            >
              <el-option v-for="activity in activityOptions" :key="activity.id" :label="activity.activity_name" :value="`activity_${activity.id}`" />
            </el-select>
            <el-input 
              v-else
              v-model="formData.link_value" 
              placeholder="请输入外部链接URL，如：https://example.com" 
            />
          </el-form-item>
  
          <el-form-item label="显示位置">
            <el-radio-group v-model="formData.position">
              <el-radio label="home">首页</el-radio>
              <el-radio label="activity">活动页</el-radio>
            </el-radio-group>
          </el-form-item>
  
          <el-form-item label="排序">
            <el-input-number v-model="formData.sort_order" :min="0" :max="999" />
          </el-form-item>
  
          <el-form-item label="状态">
            <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="禁用" />
          </el-form-item>
        </el-form>
  
        <template #footer>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import { ref, reactive, onMounted } from 'vue'
  import { ElMessage, ElLoading } from 'element-plus'
  import Cookies from 'js-cookie'
  import axios from 'axios'
  import config from '@/config'
  import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
  import { Plus, Edit, Delete } from '@element-plus/icons-vue'
  
  export default {
    name: 'BannerManagementPage',
    components: { Plus, Edit, Delete },
    setup() {
      const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
      const agentId = ref(userInfo.agent_id || 0)
      const storeId = ref(userInfo.id)
  
      const loading = ref(false)
      const submitting = ref(false)
      const searchKey = ref('')
      const positionFilter = ref(null)
      const statusFilter = ref(null)
      const bannerList = ref([])
      const productOptions = ref([])
      const activityOptions = ref([])
      const stats = ref({})
  
      const dialogVisible = ref(false)
      const isEditMode = ref(false)
      const formRef = ref(null)
      const imageFiles = ref([])
  
      const formData = reactive({
        id: null,
        title: '',
        image_url: '',
        link_type: 'url',
        link_value: '',
        position: 'home',
        status: 1,
        sort_order: 0
      })
  
      const formRules = {
        image_url: [{ required: true, message: '请上传Banner图片', trigger: 'change' }],
        link_type: [{ required: true, message: '请选择链接类型', trigger: 'change' }],
        link_value: [{ required: true, message: '请输入链接值', trigger: 'blur' }]
      }
  
      const pagination = reactive({
        page: 1,
        limit: 10,
        total: 0
      })
  
      const getLinkTypeText = (type) => {
        const map = { product: '商品', activity: '活动', url: '外链' }
        return map[type] || type
      }
  
      const onLinkTypeChange = () => {
        formData.link_value = ''
      }
  
      // 上传图片函数
      const uploadImage = async (file) => {
        if (!file.type.startsWith('image/')) {
          ElMessage.error('只能上传图片文件')
          return null
        }
        if (file.size / 1024 / 1024 > 5) {
          ElMessage.error('图片大小不能超过 5MB')
          return null
        }
  
        try {
          const signature = await getUploadSignature('image')
          const ossRes = await uploadToOSS(file, signature)
          return ossRes.url
        } catch (error) {
          console.error('上传失败:', error)
          ElMessage.error('上传失败')
          return null
        }
      }
  
      // 图片上传处理
      const handleImageChange = async (file) => {
        if (!file.raw) return
        const url = await uploadImage(file.raw)
        if (url) {
          formData.image_url = url
          imageFiles.value = [{ uid: file.uid, name: file.name, url }]
          ElMessage.success('上传成功')
        }
      }
  
      // 获取商品列表
      const fetchProducts = async () => {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, {
            params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
          })
          if (response.data.code === 200) {
            productOptions.value = response.data.data.list
          }
        } catch (error) {
          console.error('获取商品失败:', error)
        }
      }
  
      // 获取活动列表
      const fetchActivities = async () => {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/activity_api.php`, {
            params: { action: 'activities', agent_id: agentId.value, store_id: storeId.value, limit: 100 }
          })
          if (response.data.code === 200) {
            activityOptions.value = response.data.data.list
          }
        } catch (error) {
          console.error('获取活动失败:', error)
        }
      }
  
      // 获取Banner列表
      const fetchData = async () => {
        loading.value = true
        try {
          const params = {
            agent_id: agentId.value,
            store_id: storeId.value,
            page: pagination.page,
            limit: pagination.limit,
            search: searchKey.value,
            position: positionFilter.value,
            status: statusFilter.value
          }
  
          const response = await axios.get(`${config.BASE_URL}/shop/api/banner_api.php`, { params })
  
          if (response.data.code === 200) {
            bannerList.value = response.data.data.list
            stats.value = response.data.data.stats
            pagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取Banner列表失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          loading.value = false
        }
      }
  
      // 重置搜索
      const resetSearch = () => {
        searchKey.value = ''
        positionFilter.value = null
        statusFilter.value = null
        pagination.page = 1
        fetchData()
      }
  
      // 打开新增弹窗
      const openAddDialog = () => {
        isEditMode.value = false
        imageFiles.value = []
        Object.assign(formData, {
          id: null,
          title: '',
          image_url: '',
          link_type: 'url',
          link_value: '',
          position: 'home',
          status: 1,
          sort_order: 0
        })
        dialogVisible.value = true
      }
  
      // 打开编辑弹窗
      const openEditDialog = (row) => {
        isEditMode.value = true
        Object.assign(formData, row)
        
        if (row.image_url) {
          imageFiles.value = [{ uid: Date.now(), name: 'banner', url: row.image_url }]
        }
        
        dialogVisible.value = true
      }
  
      // 提交表单
      const submitForm = async () => {
        if (!formRef.value) return
  
        await formRef.value.validate(async (valid) => {
          if (!valid) return
  
          submitting.value = true
          const loadingInstance = ElLoading.service({ text: '保存中...' })
  
          try {
            const payload = {
              agent_id: agentId.value,
              store_id: storeId.value,
              ...formData
            }
  
            let response
            if (isEditMode.value) {
              payload.id = formData.id
              response = await axios.put(`${config.BASE_URL}/shop/api/banner_api.php`, payload)
            } else {
              response = await axios.post(`${config.BASE_URL}/shop/api/banner_api.php`, payload)
            }
  
            if (response.data.code === 200) {
              ElMessage.success(isEditMode.value ? '更新成功' : '创建成功')
              dialogVisible.value = false
              fetchData()
            } else {
              ElMessage.error(response.data.message || '操作失败')
            }
          } catch (error) {
            console.error('提交失败:', error)
            ElMessage.error('操作失败')
          } finally {
            submitting.value = false
            loadingInstance.close()
          }
        })
      }
  
      // 删除
      const handleDelete = async (id) => {
        const loadingInstance = ElLoading.service({ text: '删除中...' })
        try {
          const response = await axios.delete(`${config.BASE_URL}/shop/api/banner_api.php`, {
            data: { id, store_id: storeId.value }
          })
          if (response.data.code === 200) {
            ElMessage.success('删除成功')
            fetchData()
          } else {
            ElMessage.error(response.data.message || '删除失败')
          }
        } catch (error) {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        } finally {
          loadingInstance.close()
        }
      }
  
      onMounted(() => {
        fetchProducts()
        fetchActivities()
        fetchData()
      })
  
      return {
        loading,
        submitting,
        searchKey,
        positionFilter,
        statusFilter,
        bannerList,
        productOptions,
        activityOptions,
        stats,
        pagination,
        dialogVisible,
        isEditMode,
        formRef,
        formData,
        formRules,
        imageFiles,
        getLinkTypeText,
        onLinkTypeChange,
        handleImageChange,
        resetSearch,
        openAddDialog,
        openEditDialog,
        submitForm,
        handleDelete,
        fetchData
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .banner-management {
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      
      .page-title {
        font-size: 24px;
        font-weight: 600;
        color: #1a1a2e;
        margin-bottom: 4px;
      }
      
      .page-desc {
        font-size: 14px;
        color: #8c8c8c;
      }
    }
    
    .stat-cards {
      display: flex;
      gap: 16px;
      margin-bottom: 24px;
      
      .stat-card {
        background: #fff;
        border-radius: 12px;
        padding: 16px 24px;
        text-align: center;
        min-width: 120px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        
        &.success .stat-value { color: #67c23a; }
        
        .stat-value {
          font-size: 28px;
          font-weight: 700;
        }
        
        .stat-label {
          font-size: 13px;
          color: #8c8c8c;
          margin-top: 4px;
        }
      }
    }
    
    .filter-bar {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 20px;
    }
    
    .pagination-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;
    }
    
    .upload-tip {
      font-size: 12px;
      color: #8c8c8c;
      margin-top: 8px;
    }
  }
  </style>